import { useDispatch, useSelector } from "react-redux";
import {
  OpenInNew as OpenInNewIcon,
  Description as DescriptionIcon,
  Folder as FolderIcon,
  AccountCircle as AccountCircleIcon,
} from "@mui/icons-material";
import {
  Checkbox,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Drawer,
  CircularProgress,
  Button,
  Box,
  Chip,
  Avatar,
  Typography,
  Link,
} from "@mui/material";
import {
  actionSharedDataFilesComponentToggled,
  actionSheetFileUploaded,
  actionSheetFileRemoved,
  actionSheetFileUpdated,
} from "./index";
import { fetchSharedDataFile, openSharedDataFile } from "./sharedDatasetApi.js";
import {
  defaultSheetFile,
  preFormatJson,
  reduxSheetFile,
  FILE_EXT_BY_MIME_TYPE,
} from "./spreadsheetutils.js";

const drawer_ = (theme) => ({
  width: "100vw",
  minWidth: 335,
  maxWidth: 600,
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.up("md")]: {
    width: "80vw",
  },
  [theme.breakpoints.down("md")]: {
    width: "90vw",
  },
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column-reverse",
    width: "100vw",
  },
  overflow: "hidden",
});

const topBar_ = (theme) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: "8px",
  paddingRight: "16px",
  zIndex: 1,
  backgroundColor: theme.palette.background.paper,
});

const list_ = (theme) => ({
  overflowY: "auto",
  height: "100%",
  "& .MuiListItem-root:nth-of-type(odd)": {
    backgroundColor: "#f5f5f5",
  },
  "& .MuiListItem-root:nth-of-type(even)": {
    backgroundColor: "#ffffff",
  },
});

const sharedItem_ = (theme) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  paddingTop: 0,
  paddingBottom: 0,
});

const sharedItemText_ = (theme) => ({
  flexGrow: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const btnSecondary_ = (theme) => ({
  color: "black",
  backgroundColor: "#E0E0E0",
});

const noFilesBox_ = (theme) => ({
  padding: 5,
  height: "100%",
});

const chip_ = (theme) => ({
  marginRight: 1,
});

const avatar_ = (theme) => ({
  width: 24,
  minWidth: 24,
  height: 24,
  minHeight: 24,
  marginRight: 2,
});

export default function SharedDatasetFileList() {
  const dispatch = useDispatch();
  const open = useSelector(
    (state) => state.spreadsheetReducer.sharedDataFilesComponentOpen,
  );
  const files = useSelector(
    (state) => state.spreadsheetReducer.sharedDataFilesArray,
  );

  const handleDrawerDismissal = () => {
    dispatch(actionSharedDataFilesComponentToggled());
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleDrawerDismissal}
      slotProps={{ paper: { sx: drawer_ } }}
    >
      <Box sx={topBar_}>
        <Button
          sx={btnSecondary_}
          variant="contained"
          onClick={handleDrawerDismissal}
        >
          Done
        </Button>
      </Box>
      <FileList files={files} />
    </Drawer>
  );
}

const FileListItem = ({ file }) => {
  const dispatch = useDispatch();
  const idToken = useSelector((state) => state.authReducer.idToken);
  const { files = [] } = useSelector((state) => state.spreadsheetReducer);
  if (!file) {
    return null;
  }
  const { name, id } = file;
  const reduxFile = files.find((f) => f.sharedDataId === id);
  const status = reduxFile?.status;

  const handleCheck = () => {
    if (status === "loading") {
      return;
    }
    if (status === "complete") {
      dispatch(actionSheetFileRemoved({ id: reduxFile.id }));
    } else {
      syncFileContent(id);
    }
  };

  const syncFileContent = async (fileId) => {
    const nextId =
      files.length > 0 ? Math.max(...files.map((f) => f.id)) + 1 : 0;

    try {
      const sheetData = defaultSheetFile(nextId);
      const reduxData = reduxSheetFile();
      dispatch(
        actionSheetFileUploaded({
          ...sheetData,
          id: nextId,
          json: [[], []],
          fileName: name,
          sharedDataId: fileId,
          source: "google",
          status: "loading",
        }),
      );
      const { data: rawJson } = await fetchSharedDataFile(fileId, idToken);
      const { headerRowIndex, rmrecnbr, lnglat, type, json, preHeaderJson } =
        preFormatJson(rawJson);
      dispatch(
        actionSheetFileUpdated({
          id: nextId,
          rmrecnbr: rmrecnbr,
          lnglat: lnglat,
          json: json,
          preHeaderJson: preHeaderJson,
          type: type,
          defaultText: rmrecnbr,
          headerRowIndex: headerRowIndex,
          status: "complete",
          ...reduxData,
        }),
      );
    } catch (e) {
      console.error("Failed to fetch file content: " + e.message);
      dispatch(
        actionSheetFileUpdated({
          id: nextId,
          status: "error",
        }),
      );
    }
  };

  return (
    <ListItem divider key={id} sx={sharedItem_}>
      <FileListItemAvatar reduxFile={reduxFile} />
      <ListItemText
        primary={<Typography noWrap>{name}</Typography>}
        secondary={<ListItemSecondaryComponent file={file} />}
        slots={{
          secondary: "div",
        }}
        slotProps={{
          primary: {
            variant: "body1",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "1.2rem",
          },
        }}
        sx={sharedItemText_}
        onClick={handleCheck}
      />
      <FileListItemActions
        file={file}
        reduxFile={reduxFile}
        handleCheck={handleCheck}
      />
    </ListItem>
  );
};

const FileList = ({ files }) => {
  if (!files) {
    return null;
  }
  if (files.length === 0) {
    return (
      <Box sx={noFilesBox_}>
        <Typography variant="body1" align="left" gutterBottom>
          {`Ope! Looks like you don't yet have access to any Google Sheets shared with MGIS.`}
        </Typography>
        <Typography variant="body1" align="left" gutterBottom>
          {`To get started visualizing space in MGIS with data from Google Drive, visit our `}
          <Link
            href="https://docs.google.com/document/d/1e9JAh4edWLZx4Mko7GQVxnRwQ0q2a3a6xXsS5J7SDHY/edit?tab=t.rvf178s3jjeo#heading=h.1gpfaxy7zixh"
            target="_blank"
            rel="noopener"
          >
            Documentation
          </Link>
          {`.`}
        </Typography>
        <Typography variant="body1" align="left">
          {`Once you've connected a dataset, it will appear here.`}
        </Typography>
      </Box>
    );
  }
  return (
    <List sx={list_}>
      {files.map((file) => (
        <FileListItem file={file} key={file.id} />
      ))}
    </List>
  );
};

const FileListItemAvatar = ({ reduxFile }) => {
  const id = reduxFile?.id;
  const { fillColor: defaultFill } = defaultSheetFile(id);
  const fillColor = reduxFile?.fillColor || defaultFill;
  const status = reduxFile?.status;
  if (status === "complete") {
    return (
      <Avatar
        sx={[
          avatar_,
          {
            bgcolor: fillColor,
            "& .MuiAvatar-fallback": { display: "none" },
          },
        ]}
      >
        {null}
      </Avatar>
    );
  }
  if (status === "loading") {
    return <CircularProgress size={24} sx={avatar_} />;
  }
  return <Box sx={avatar_}></Box>;
};

const FileListItemActions = ({ file, handleCheck, reduxFile }) => {
  const { id, mimeType } = file;
  const status = reduxFile?.status;

  const handleOpen = () => {
    openSharedDataFile(id, mimeType);
  };

  return (
    <>
      <Tooltip describeChild title="Sync Data from Google Drive">
        <Checkbox
          slotProps={{ input: { "aria-label": `Open ${file.name}` } }}
          disabled={status === "loading"}
          checked={
            status === "loading" || status === "complete" || status === "error"
          }
          onChange={handleCheck}
        />
      </Tooltip>
      <Tooltip title="Open in Drive">
        <IconButton
          aria-label="Open File in Google Drive in a new tab"
          size="small"
          onClick={handleOpen}
        >
          <OpenInNewIcon />
        </IconButton>
      </Tooltip>
    </>
  );
};

const ListItemSecondaryComponent = ({ file }) => {
  const { mimeType, owner, parent } = file;
  return (
    <Box sx={{ display: "flex", flexDirection: "row" }}>
      {mimeType && (
        <Chip
          icon={<DescriptionIcon />}
          label={FILE_EXT_BY_MIME_TYPE[mimeType]}
          size="small"
          variant="outlined"
          sx={chip_}
        />
      )}
      {owner && (
        <Chip
          icon={<AccountCircleIcon />}
          label={owner}
          variant="outlined"
          size="small"
          sx={chip_}
        />
      )}
      {parent && (
        <Chip
          icon={<FolderIcon />}
          label={parent}
          variant="outlined"
          size="small"
          sx={{ maxWidth: "300px" }}
          slotProps={{
            label: {
              overflow: "hidden",
            },
          }}
        />
      )}
    </Box>
  );
};
